import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_ADMIN_PERMISSIONS = {
  music: true,
  photos: true,
  videos: true,
  news: true,
  events: true,
  submissions: true,
  chat: true,
  about: true,
  users: false,
  settings: false,
  ai_studio: false,
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('beatlab_token');
    if (savedToken) {
      setToken(savedToken);
      fetchUser(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (authToken) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('beatlab_token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: authToken, user: userData } = response.data;
    localStorage.setItem('beatlab_token', authToken);
    setToken(authToken);
    setUser(userData);
    return userData;
  };

  const register = async (username, email, password, invitation_code) => {
    await axios.post(`${API}/auth/register`, { username, email, password, invitation_code: invitation_code || undefined });
    return login(email, password);
  };

  const logout = async () => {
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    localStorage.removeItem('beatlab_token');
    setToken(null);
    setUser(null);
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const isOwner = user?.role === 'owner';

  const getPermissions = useCallback(() => {
    if (!user) return {};
    if (user.role === 'owner') {
      return Object.fromEntries(Object.keys(DEFAULT_ADMIN_PERMISSIONS).map(k => [k, true]));
    }
    if (user.role === 'admin') {
      const userPerms = user.permissions || {};
      return { ...DEFAULT_ADMIN_PERMISSIONS, ...userPerms };
    }
    return {};
  }, [user]);

  const hasPermission = useCallback((key) => {
    if (!user) return false;
    if (user.role === 'owner') return true;
    if (user.role === 'admin') {
      const perms = user.permissions || DEFAULT_ADMIN_PERMISSIONS;
      return perms[key] !== false;
    }
    return false;
  }, [user]);

  const refreshUser = useCallback(async () => {
    if (token) {
      await fetchUser(token);
    }
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        isAdmin,
        isOwner,
        isAuthenticated: !!user,
        getPermissions,
        hasPermission,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
